import type { Order } from '@auto-oms/shared/types/order';
import { StatusBadge } from './StatusBadge';
import { ChannelBadge } from './ChannelBadge';
import { formatDateTime, formatKRW } from '../../lib/format';

interface Props {
  open: boolean;
  order: Order | null;
  onClose: () => void;
}

export function OrderDetailDialog({ open, order, onClose }: Props) {
  if (!open || !order) return null;
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-ink-950/40 backdrop-blur-[2px] sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="flex w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-paper shadow-modal sm:rounded-2xl"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-ink-200 px-5 py-4 sm:px-7 sm:py-5">
          <div className="min-w-0">
            <p className="label-overline">주문 상세</p>
            <h2
              id="order-detail-title"
              className="mt-1 truncate text-[18px] font-medium tracking-[-0.01em] text-ink-950 sm:text-[20px]"
            >
              {order.productName}
            </h2>
            <p className="mt-1 truncate font-mono text-[12px] text-ink-500">
              {order.externalOrderId}
            </p>
          </div>
          <button type="button" className="btn-secondary btn-sm shrink-0" onClick={onClose}>
            닫기
          </button>
        </div>

        <div className="overflow-y-auto bg-canvas px-5 py-5 sm:px-7 sm:py-6">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={order.status} />
            <ChannelBadge platform={order.platform} channelName={order.channelName} />
            {!!order.isAddedProduct && (
              <span className="badge bg-info-soft text-info-ink">추가상품</span>
            )}
          </div>

          <dl className="mt-5 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <DetailRow label="상품명" value={order.productName} />
            {order.productOption && <DetailRow label="옵션" value={order.productOption} />}
            {(order.pCode || order.productId) && (
              <DetailRow
                label="품번"
                value={[
                  order.pCode && `품번:${order.pCode}`,
                  order.productId && `채널품번:${order.productId}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                mono
              />
            )}
            <DetailRow label="수량" value={`${order.quantity}개`} />
            <DetailRow label="합계" value={formatKRW(order.totalPrice)} />
            <DetailRow label="단가" value={formatKRW(order.unitPrice)} />
            <DetailRow label="주문일시" value={formatDateTime(order.orderedAt)} />
            {order.paidAt && <DetailRow label="결제일시" value={formatDateTime(order.paidAt)} />}
            <DetailRow label="구매자" value={order.buyerName} />
            <DetailRow label="수령인" value={order.recipientName} />
            <DetailRow label="연락처" value={order.recipientPhone} />
            <DetailRow
              label="주소"
              value={`(${order.recipientZip}) ${order.recipientAddress}`}
              span2
            />
            {order.deliveryMessage && (
              <DetailRow label="배송메시지" value={order.deliveryMessage} span2 />
            )}
            {order.deliveryType && <DetailRow label="배송방식" value={order.deliveryType} />}
            <DetailRow label="택배사" value={order.trackingCarrier ?? '—'} />
            <DetailRow label="송장번호" value={order.trackingNumber ?? '—'} mono />
            {order.shippedAt && (
              <DetailRow label="출고일시" value={formatDateTime(order.shippedAt)} />
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
  span2,
  vertical,
}: {
  label: string;
  value: string;
  mono?: boolean;
  span2?: boolean;
  vertical?: boolean;
}) {
  if (vertical) {
    return (
      <div className={span2 ? 'sm:col-span-2' : ''}>
        <dt className="label-overline">{label}</dt>
        <dd
          className={[
            'mt-1 break-words text-[13px] text-ink-900',
            mono ? 'font-mono text-[12px] tabular' : '',
          ].join(' ')}
        >
          {value}
        </dd>
      </div>
    );
  }
  return (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <div className="flex items-baseline gap-3 sm:block">
        <dt className="label-overline w-20 shrink-0">{label}</dt>
        <dd
          className={[
            'min-w-0 break-words text-left text-[13px] text-ink-900 sm:mt-1',
            mono ? 'font-mono text-[12px] tabular' : '',
          ].join(' ')}
        >
          {value}
        </dd>
      </div>
    </div>
  );
}
